import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Stage, Layer, Group, Rect, Text } from 'react-konva';
import GridLayer, { CELL_SIZE } from './GridLayer';
import TerrainLayer from './TerrainLayer';
import WallLayer from './WallLayer';
import PathLayer from './PathLayer';
import MeasureLayer from './MeasureLayer';
import DoorSegmentLayer from './DoorSegmentLayer';
import FogLayer from './FogLayer';
import TokenLayer from './TokenLayer';
import PingLayer from './PingLayer';
import TrapLayer from './TrapLayer';
import TeleportLayer from './TeleportLayer';
import { useMap } from '../../contexts/MapContext';
import { useCampaign } from '../../contexts/CampaignContext';
import * as tokenAnimator from '../../lib/tokenAnimator';

const MIN_SCALE = 0.25;
const MAX_SCALE = 3;

const MapCanvas = forwardRef(function MapCanvas({
  isDm,
  onTokenSelect,
  selectedTokenId,
  hoveredTokenId = null,
  editorMode    = null,
  activeTerrain = 'stone_floor',
  activeWallStyle = 'stone',
  activeDoorStyle = 'wood',
  activeDoorType  = 'door',
  activeElevation = 0,
  wallCurved      = false,
  activePathStyle = 'dirt',
  pathCurved      = false,
  activeTeleportStyle = 'portal',
  teleportTwoWay      = true,
  teleportHidden      = false,
  teleportDC          = 12,
  myTokenId     = null,
  followTokenId = null,
  onCellClick   = null,
  onDoorClick   = null,
  selectedDoorId = null,
  onSpawnSet    = null,
  autoFitBounds    = null,
  readOnly         = false,
  highlightTokenIds = null,
}, ref) {
  const containerRef  = useRef(null);
  const stageRef      = useRef(null);
  const [size, setSize]   = useState({ w: window.innerWidth, h: window.innerHeight });
  const [scale, setScale] = useState(1);
  const [pos,   setPos]   = useState({ x: 0, y: 0 });

  // Mirror pos / scale / size into refs so the camera-follow effect can read
  // the current stage transform without re-running on every pan/zoom tick.
  const posRef   = useRef(pos);
  const scaleRef = useRef(scale);
  const sizeRef  = useRef(size);
  useEffect(() => { posRef.current = pos; }, [pos]);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { sizeRef.current = size; }, [size]);

  const isPainting        = useRef(false);
  const paintedThisStroke = useRef(new Set());
  const didDrag           = useRef(false);
  const { paintCells, paintElevations, fillAt, paintFogCells, cells, gridW, gridH, spawnPoint,
          walls, addWall, updateWall, deleteWall, removeWallSegment,
          addDoor,
          paths, addPath, updatePath, deletePath, removePathSegment, ambient } = useMap();
  const { tokens, createTrap, traps, createTeleport } = useCampaign();

  // ── Wall drawing state ─────────────────────────────────────────────────────
  const draftWallPtsRef   = useRef([]);          // source-of-truth ref (sync)
  const [draftWallPts,    setDraftWallPts]    = useState([]);   // for render
  const [wallMousePos,    setWallMousePos]    = useState(null);
  const [selectedWallIds, setSelectedWallIds] = useState(() => new Set());
  const selectedWallIdsRef = useRef(selectedWallIds);
  useEffect(() => { selectedWallIdsRef.current = selectedWallIds; }, [selectedWallIds]);
  const copiedWallsRef = useRef([]);
  const [wallDragOffset, setWallDragOffset] = useState(null);
  const wallDragRef = useRef(null); // { startGx, startGy }
  const shiftDownRef = useRef(false);

  // ── Door pen state ─────────────────────────────────────────────────────────
  const [draftDoorStart,  setDraftDoorStart]  = useState(null);
  const [doorMousePos,    setDoorMousePos]    = useState(null);

  // ── Teleport pen state ────────────────────────────────────────────────────
  const [draftTeleportStart, setDraftTeleportStart] = useState(null);
  const [teleportMousePos,   setTeleportMousePos]   = useState(null);

  // ── Path drawing state (mirrors wall state) ───────────────────────────────
  const draftPathPtsRef   = useRef([]);
  const [draftPathPts,    setDraftPathPts]    = useState([]);
  const [pathMousePos,    setPathMousePos]    = useState(null);
  const [selectedPathIds, setSelectedPathIds] = useState(() => new Set());
  const selectedPathIdsRef = useRef(selectedPathIds);
  useEffect(() => { selectedPathIdsRef.current = selectedPathIds; }, [selectedPathIds]);
  const copiedPathsRef = useRef([]);
  const [pathDragOffset, setPathDragOffset] = useState(null);
  const pathDragRef = useRef(null);

  const setDraftWall = useCallback((pts) => {
    draftWallPtsRef.current = pts;
    setDraftWallPts([...pts]);
  }, []);

  const setDraftPath = useCallback((pts) => {
    draftPathPtsRef.current = pts;
    setDraftPathPts([...pts]);
  }, []);

  // ── Camera follow ────────────────────────────────────────────────────────
  // Continuous rAF lerp toward the followed token's interpolated position.
  // Subscribing to `tokenAnimator` rather than just the committed Firestore
  // x/y means the camera tracks during the smooth walk animation, not just
  // when the move commits — and works identically on touch and mouse devices
  // since it doesn't depend on any pointer event sequence.
  const followStateRef = useRef({ id: null, x: null, y: null, size: 1 });
  const followToken   = followTokenId ? tokens[followTokenId] : null;
  const ftx = followToken?.x;
  const fty = followToken?.y;
  const ftSize = followToken?.size || 1;
  // Ref to kick() so the followState update effect below can wake the camera
  // loop after it has gone idle (token coords changed → catch up).
  const kickFollowRef = useRef(null);

  useEffect(() => {
    followStateRef.current = { id: followTokenId, x: ftx, y: fty, size: ftSize };
    kickFollowRef.current?.();
  }, [followTokenId, ftx, fty, ftSize]);

  useEffect(() => {
    if (!followTokenId) {
      kickFollowRef.current = null;
      return;
    }
    let cancelled = false;
    let rafId = 0;
    let running = false;

    const tick = () => {
      if (cancelled) return;
      const { id, x, y, size: tSize } = followStateRef.current;
      let needsMore = false;
      if (id != null && x != null && y != null) {
        const live = tokenAnimator.getDisplayPos(id, x, y);
        const sc   = scaleRef.current;
        const win  = sizeRef.current;
        const cx   = (live.x + tSize / 2) * CELL_SIZE;
        const cy   = (live.y + tSize / 2) * CELL_SIZE;
        const targetX = win.w / 2 - cx * sc;
        const targetY = win.h / 2 - cy * sc;
        const cur = posRef.current;
        const dx = targetX - cur.x;
        const dy = targetY - cur.y;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          const lerpF = 0.18;
          setPos({ x: cur.x + dx * lerpF, y: cur.y + dy * lerpF });
          needsMore = true;
        } else if (tokenAnimator.isAnimating(id)) {
          // Camera is settled but the followed token is still tweening — keep
          // ticking so we can react when the tween moves.
          needsMore = true;
        }
      }
      if (needsMore) {
        rafId = requestAnimationFrame(tick);
      } else {
        running = false;
        rafId = 0;
      }
    };

    const kick = () => {
      if (cancelled || running) return;
      running = true;
      rafId = requestAnimationFrame(tick);
    };
    kickFollowRef.current = kick;

    // Subscribe to tokenAnimator so animation starts kick us awake without
    // the camera having to poll at 60Hz.
    const unsubscribe = tokenAnimator.subscribe(kick);

    kick();

    return () => {
      cancelled = true;
      kickFollowRef.current = null;
      unsubscribe();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [followTokenId]);

  // ── Measure tool state (click-and-drag distance) ──────────────────────────
  const [measureFrom, setMeasureFrom] = useState(null);
  const [measureTo,   setMeasureTo]   = useState(null);
  const measuringRef = useRef(false);
  useEffect(() => {
    if (editorMode !== 'measure') {
      setMeasureFrom(null);
      setMeasureTo(null);
      measuringRef.current = false;
    }
    if (editorMode !== 'teleport-pen') {
      setDraftTeleportStart(null);
      setTeleportMousePos(null);
    }
  }, [editorMode]);

  // ── Snap screen coords → 0.5-cell grid coords ──────────────────────────────
  const screenToSnappedGrid = useCallback((stage, clientX, clientY) => {
    if (!stage) return null;
    const rect = stage.container().getBoundingClientRect();
    const transform = stage.getAbsoluteTransform().copy().invert();
    const p  = transform.point({ x: clientX - rect.left, y: clientY - rect.top });
    const gx = Math.round(p.x / CELL_SIZE * 2) / 2;
    const gy = Math.round(p.y / CELL_SIZE * 2) / 2;
    return { x: gx, y: gy };
  }, []);

  useImperativeHandle(ref, () => ({
    panBy(dx, dy) { setPos((prev) => ({ x: prev.x - dx, y: prev.y - dy })); },
    getViewCenter() {
      const s = stageRef.current;
      if (!s) return { x: 0, y: 0 };
      const cx = (-s.x() + size.w / 2) / s.scaleX();
      const cy = (-s.y() + size.h / 2) / s.scaleY();
      return { x: Math.floor(cx / CELL_SIZE), y: Math.floor(cy / CELL_SIZE) };
    },
  }), [size]);

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      setSize({ w: containerRef.current.offsetWidth, h: containerRef.current.offsetHeight });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // ── Auto-fit to revealed bounds ─────────────────────────────────────────
  useEffect(() => {
    if (!autoFitBounds || !containerRef.current) return;
    const { minX, minY, maxX, maxY } = autoFitBounds;
    if (maxX < minX || maxY < minY) return;

    const pad      = 1.5;
    const areaW    = (maxX - minX + 1 + pad * 2) * CELL_SIZE;
    const areaH    = (maxY - minY + 1 + pad * 2) * CELL_SIZE;
    const viewW    = containerRef.current.offsetWidth;
    const viewH    = containerRef.current.offsetHeight;

    const newScale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, Math.min(viewW / areaW, viewH / areaH))
    );

    const centerGridX = ((minX + maxX + 1) / 2) * CELL_SIZE;
    const centerGridY = ((minY + maxY + 1) / 2) * CELL_SIZE;
    const newX = viewW / 2 - centerGridX * newScale;
    const newY = viewH / 2 - centerGridY * newScale;

    setScale(newScale);
    setPos({ x: newX, y: newY });
  }, [autoFitBounds]);

  const screenToCell = useCallback((stage, screenX, screenY) => {
    const rect = stage.container().getBoundingClientRect();
    const transform = stage.getAbsoluteTransform().copy().invert();
    const p = transform.point({ x: screenX - rect.left, y: screenY - rect.top });
    const gx = Math.floor(p.x / CELL_SIZE);
    const gy = Math.floor(p.y / CELL_SIZE);
    if (gx < 0 || gx >= gridW || gy < 0 || gy >= gridH) return null;
    return { x: gx, y: gy };
  }, [gridW, gridH]);

  // ── Paint-batch: accumulate painted cells across a brush stroke and flush
  // them to MapContext once per animation frame. Collapses N state updates
  // (one per mouse sample) into 1, without changing what gets painted.
  const paintBatch = useRef({ kind: null, value: null, coords: [] });
  const paintBatchRaf = useRef(0);
  const flushPaintBatch = useCallback(() => {
    paintBatchRaf.current = 0;
    const { kind, value, coords } = paintBatch.current;
    if (!coords.length) return;
    paintBatch.current = { kind: null, value: null, coords: [] };
    if (kind === 'terrain') paintCells(coords, value);
    else if (kind === 'elevation') paintElevations(coords, value);
    else if (kind === 'fog') paintFogCells(coords, value);
  }, [paintCells, paintElevations, paintFogCells]);

  const applyPaint = useCallback((cell) => {
    if (!editorMode || editorMode === 'spawn') return;
    let kind = null, value = null;
    if (editorMode === 'fog-reveal')     { kind = 'fog'; value = true; }
    else if (editorMode === 'fog-hide')  { kind = 'fog'; value = false; }
    else if (editorMode === 'eraser')    { kind = 'terrain'; value = 'empty'; }
    else if (editorMode === 'paint')     { kind = 'terrain'; value = activeTerrain; }
    else if (editorMode === 'elevation') { kind = 'elevation'; value = activeElevation; }
    if (!kind) return;

    const current = paintBatch.current;
    // Flush immediately if the paint kind/value changes mid-batch (shouldn't
    // happen during one stroke, but be safe).
    if (current.kind && (current.kind !== kind || current.value !== value)) {
      flushPaintBatch();
    }
    paintBatch.current = {
      kind,
      value,
      coords: [...(paintBatch.current.coords || []), [cell.x, cell.y]],
    };
    if (!paintBatchRaf.current) {
      paintBatchRaf.current = requestAnimationFrame(flushPaintBatch);
    }
  }, [editorMode, activeTerrain, activeElevation, flushPaintBatch]);

  const handleStageMouseDown = useCallback((e) => {
    didDrag.current = false;
    if (!editorMode || e.evt.button !== 0) return;

    // ── Wall pen — click to add points, double-click to finish ────────────
    if (editorMode === 'wall-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (!pt) return;
      const prev = draftWallPtsRef.current;
      // Check if clicking near start point to close the wall
      if (prev.length >= 3) {
        const d = Math.hypot(pt.x - prev[0].x, pt.y - prev[0].y);
        if (d < 0.75) {
          addWall({ id: `w${Date.now()}`, pts: [...prev], style: activeWallStyle, closed: true, curved: wallCurved });
          setDraftWall([]);
          setWallMousePos(null);
          return;
        }
      }
      // Add point to the draft
      setDraftWall([...prev, pt]);
      return;
    }

    // ── Wall select / eraser — handled by WallLayer clicks ──────────────
    if (editorMode === 'wall-select' || editorMode === 'wall-eraser') return;

    // ── Path pen — click to add points, double-click to finish ──────────
    if (editorMode === 'path-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (!pt) return;
      const prev = draftPathPtsRef.current;
      setDraftPath([...prev, pt]);
      return;
    }

    // ── Path select / eraser — handled by PathLayer clicks ─────────────
    if (editorMode === 'path-select' || editorMode === 'path-eraser') return;

    // ── Measure — click-and-drag for distance readout ─────────────────
    if (editorMode === 'measure') {
      const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (!cell) return;
      setMeasureFrom(cell);
      setMeasureTo(cell);
      measuringRef.current = true;
      return;
    }

    // ── Door pen — two clicks to place a door segment ─────────────────────
    if (editorMode === 'door-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (!pt) return;
      if (!draftDoorStart) {
        setDraftDoorStart(pt);
      } else {
        const hp = activeDoorType === 'bars' ? 20 : 15;
        const isSecret = activeDoorType === 'secret';
        addDoor({
          id: `door_${Date.now()}`,
          pts: [draftDoorStart, pt],
          type: activeDoorType,
          style: activeDoorStyle,
          state: 'closed',
          hp, maxHp: hp,
          secretRevealed: false,
          secretWallStyle: activeWallStyle,
          // Secret doors get a default discovery DC so perception can find
          // them out of the box. DM can adjust in DoorPanel.
          ...(isSecret ? { discoveryDC: 12 } : {}),
        });
        setDraftDoorStart(null);
        setDoorMousePos(null);
      }
      return;
    }

    // ── Teleport pen — two clicks to link endpoint A to endpoint B ───────
    if (editorMode === 'teleport-pen') {
      const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (!cell) return;
      if (!draftTeleportStart) {
        setDraftTeleportStart({ x: cell.x, y: cell.y });
        setTeleportMousePos({ x: cell.x, y: cell.y });
      } else {
        // Don't allow A==B; ignore the click instead so the DM can pick a
        // different cell rather than landing in a confusing committed state.
        if (draftTeleportStart.x === cell.x && draftTeleportStart.y === cell.y) return;
        const isTrapdoor = activeTeleportStyle === 'trapdoor';
        createTeleport({
          a:           { x: draftTeleportStart.x, y: draftTeleportStart.y },
          b:           { x: cell.x, y: cell.y },
          twoWay:      !!teleportTwoWay,
          style:       activeTeleportStyle,
          label:       null,
          hidden:      isTrapdoor && !!teleportHidden,
          discoveryDC: isTrapdoor && teleportHidden ? (teleportDC ?? 12) : null,
        });
        setDraftTeleportStart(null);
        setTeleportMousePos(null);
      }
      return;
    }

    // ── Wall-to-door — handled by WallLayer segment clicks ────────────────
    if (editorMode === 'wall-to-door') return;

    const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
    if (!cell) return;
    if (editorMode === 'spawn') { onSpawnSet?.(cell.x, cell.y); return; }
    if (editorMode === 'fill') { fillAt(cell.x, cell.y, activeTerrain); return; }
    if (editorMode === 'trap-place') {
      const defaultName = `Trap ${Object.keys(traps || {}).length + 1}`;
      const raw = window.prompt('Name this trap (e.g. Spike Pit, Poison Dart, Pressure Plate):', defaultName);
      if (raw === null) return; // user cancelled
      const label = raw.trim() || defaultName;
      createTrap({
        x: cell.x,
        y: cell.y,
        label,
        damageDie: '1d6',
        damageType: 'piercing',
        secondaryEffect: null,
        triggerType: 'enter',
        discoveryDC: 12,
        reactionDC: 12,
      });
      return;
    }
    isPainting.current = true;
    paintedThisStroke.current = new Set([`${cell.x},${cell.y}`]);
    applyPaint(cell);
  }, [editorMode, activeTerrain, screenToCell, screenToSnappedGrid, fillAt, applyPaint,
      onSpawnSet, addWall, activeWallStyle, setDraftWall, draftDoorStart, activeDoorStyle, activeDoorType, addDoor,
      setDraftPath, createTrap, traps,
      draftTeleportStart, activeTeleportStyle, teleportTwoWay, teleportHidden, teleportDC, createTeleport]);

  const handleWallMouseDown = useCallback((id, _ignoredShift, konvaEvt) => {
    const shift = shiftDownRef.current || !!konvaEvt?.evt?.shiftKey;
    const prev  = selectedWallIdsRef.current;
    if (shift) {
      // Toggle in selection; do not start a drag
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectedWallIds(next);
      return;
    }
    // Non-shift: if clicked wall is part of selection, keep selection and start drag.
    // Otherwise replace selection with this wall and start drag.
    let next = prev;
    if (!prev.has(id)) {
      next = new Set([id]);
      setSelectedWallIds(next);
    }
    const pt = screenToSnappedGrid(stageRef.current, konvaEvt.evt.clientX, konvaEvt.evt.clientY);
    if (pt) {
      wallDragRef.current = { startGx: pt.x, startGy: pt.y };
      setWallDragOffset({ x: 0, y: 0 });
    }
  }, [screenToSnappedGrid]);

  const handlePathMouseDown = useCallback((id, _ignoredShift, konvaEvt) => {
    const shift = shiftDownRef.current || !!konvaEvt?.evt?.shiftKey;
    const prev  = selectedPathIdsRef.current;
    if (shift) {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectedPathIds(next);
      return;
    }
    let next = prev;
    if (!prev.has(id)) {
      next = new Set([id]);
      setSelectedPathIds(next);
    }
    const pt = screenToSnappedGrid(stageRef.current, konvaEvt.evt.clientX, konvaEvt.evt.clientY);
    if (pt) {
      pathDragRef.current = { startGx: pt.x, startGy: pt.y };
      setPathDragOffset({ x: 0, y: 0 });
    }
  }, [screenToSnappedGrid]);

  const handleStageMouseMove = useCallback((e) => {
    if (e.evt.buttons === 1) didDrag.current = true;

    if (editorMode === 'wall-select' && wallDragRef.current) {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (pt) {
        const dx = pt.x - wallDragRef.current.startGx;
        const dy = pt.y - wallDragRef.current.startGy;
        setWallDragOffset({ x: dx, y: dy });
      }
      return;
    }

    if (editorMode === 'path-select' && pathDragRef.current) {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (pt) {
        const dx = pt.x - pathDragRef.current.startGx;
        const dy = pt.y - pathDragRef.current.startGy;
        setPathDragOffset({ x: dx, y: dy });
      }
      return;
    }

    if (editorMode === 'wall-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      setWallMousePos(pt);
      return;
    }

    if (editorMode === 'path-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      setPathMousePos(pt);
      return;
    }

    if (editorMode === 'door-pen') {
      const pt = screenToSnappedGrid(stageRef.current, e.evt.clientX, e.evt.clientY);
      setDoorMousePos(pt);
      return;
    }

    if (editorMode === 'teleport-pen') {
      const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (cell) setTeleportMousePos({ x: cell.x, y: cell.y });
      return;
    }

    if (editorMode === 'measure' && measuringRef.current) {
      const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
      if (cell) setMeasureTo(cell);
      return;
    }

    if (!isPainting.current || !editorMode || editorMode === 'fill') return;
    const cell = screenToCell(stageRef.current, e.evt.clientX, e.evt.clientY);
    if (!cell) return;
    const key = `${cell.x},${cell.y}`;
    if (paintedThisStroke.current.has(key)) return;
    paintedThisStroke.current.add(key);
    applyPaint(cell);
  }, [editorMode, screenToCell, screenToSnappedGrid, applyPaint]);

  const handleStageMouseUp = useCallback((e) => {
    if (wallDragRef.current) {
      const off = wallDragOffset;
      const ids = selectedWallIdsRef.current;
      if (off && (off.x !== 0 || off.y !== 0) && ids.size > 0) {
        for (const w of walls) {
          if (!ids.has(w.id)) continue;
          updateWall(w.id, { pts: w.pts.map((p) => ({ x: p.x + off.x, y: p.y + off.y })) });
        }
      }
      wallDragRef.current = null;
      setWallDragOffset(null);
    }
    if (measuringRef.current) {
      measuringRef.current = false;
      // Keep measureFrom/measureTo so the line stays visible after release.
    }
    if (pathDragRef.current) {
      const off = pathDragOffset;
      const ids = selectedPathIdsRef.current;
      if (off && (off.x !== 0 || off.y !== 0) && ids.size > 0) {
        for (const p of paths) {
          if (!ids.has(p.id)) continue;
          updatePath(p.id, { pts: p.pts.map((pt) => ({ x: pt.x + off.x, y: pt.y + off.y })) });
        }
      }
      pathDragRef.current = null;
      setPathDragOffset(null);
    }
    isPainting.current = false;
    paintedThisStroke.current = new Set();
    // Flush any pending paint batch immediately so the final brush hit commits
    if (paintBatchRaf.current) {
      cancelAnimationFrame(paintBatchRaf.current);
      paintBatchRaf.current = 0;
      flushPaintBatch();
    }
    // Extract pointer coords from either mouse or touch events — players ping
    // from mobile, so touchend fires here with coords on changedTouches.
    const evt = e.evt;
    const isTouch = !!evt.changedTouches;
    const client = isTouch
      ? (evt.changedTouches[0] ? { x: evt.changedTouches[0].clientX, y: evt.changedTouches[0].clientY } : null)
      : { x: evt.clientX, y: evt.clientY };
    const primaryButton = isTouch || evt.button === 0;
    if (!editorMode && !didDrag.current && onCellClick && primaryButton && client) {
      const cell = screenToCell(stageRef.current, client.x, client.y);
      if (cell) {
        const idx = cell.y * gridW + cell.x;
        onCellClick(cell.x, cell.y, cells[idx]);
      }
    }
    didDrag.current = false;
  }, [editorMode, onCellClick, screenToCell, cells, gridW, wallDragOffset, walls, updateWall, flushPaintBatch,
      pathDragOffset, paths, updatePath]);

  const handleStageDblClick = useCallback((e) => {
    if (editorMode === 'wall-pen') {
      const pts = draftWallPtsRef.current;
      if (pts.length >= 2) {
        addWall({ id: `w${Date.now()}`, pts: [...pts], style: activeWallStyle, closed: false, curved: wallCurved });
      }
      setDraftWall([]);
      setWallMousePos(null);
    }
    if (editorMode === 'path-pen') {
      const pts = draftPathPtsRef.current;
      if (pts.length >= 2) {
        addPath({ id: `p${Date.now()}`, pts: [...pts], style: activePathStyle, curved: pathCurved });
      }
      setDraftPath([]);
      setPathMousePos(null);
    }
  }, [editorMode, activeWallStyle, wallCurved, addWall, activePathStyle, pathCurved, addPath, setDraftPath]);

  // ── ESC / Delete key handlers ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && editorMode === 'wall-pen') {
        setDraftWall([]);
        setWallMousePos(null);
      }
      if (e.key === 'Escape' && editorMode === 'path-pen') {
        setDraftPath([]);
        setPathMousePos(null);
      }
      if (e.key === 'Escape' && editorMode === 'measure') {
        setMeasureFrom(null);
        setMeasureTo(null);
        measuringRef.current = false;
      }
      if (e.key === 'Escape' && editorMode === 'door-pen') {
        setDraftDoorStart(null);
        setDoorMousePos(null);
      }
      if (e.key === 'Escape' && editorMode === 'teleport-pen') {
        setDraftTeleportStart(null);
        setTeleportMousePos(null);
      }
      if (e.key === 'Shift') shiftDownRef.current = true;
      if (editorMode === 'path-select') {
        if (e.key === 'Delete' && selectedPathIds.size > 0) {
          for (const id of selectedPathIds) deletePath(id);
          setSelectedPathIds(new Set());
          return;
        }
        const tag2 = (e.target?.tagName || '').toLowerCase();
        if (tag2 === 'input' || tag2 === 'textarea' || tag2 === 'select' || e.target?.isContentEditable) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selectedPathIds.size > 0) {
          e.preventDefault();
          copiedPathsRef.current = paths
            .filter((p) => selectedPathIds.has(p.id))
            .map((p) => ({
              pts: p.pts.map((pt) => ({ x: pt.x, y: pt.y })),
              style: p.style,
              curved: !!p.curved,
            }));
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && copiedPathsRef.current.length > 0) {
          e.preventDefault();
          const OFFSET = 1;
          const newIds = new Set();
          const baseTime = Date.now();
          copiedPathsRef.current.forEach((tpl, i) => {
            const id = `p${baseTime}_${i}`;
            newIds.add(id);
            addPath({
              id,
              pts: tpl.pts.map((pt) => ({ x: pt.x + OFFSET, y: pt.y + OFFSET })),
              style: tpl.style,
              curved: tpl.curved,
            });
          });
          setSelectedPathIds(newIds);
          return;
        }
        return;
      }
      if (editorMode !== 'wall-select') return;
      if (e.key === 'Delete' && selectedWallIds.size > 0) {
        for (const id of selectedWallIds) deleteWall(id);
        setSelectedWallIds(new Set());
        return;
      }
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selectedWallIds.size > 0) {
        e.preventDefault();
        copiedWallsRef.current = walls
          .filter((w) => selectedWallIds.has(w.id))
          .map((w) => ({
            pts: w.pts.map((p) => ({ x: p.x, y: p.y })),
            style: w.style,
            closed: !!w.closed,
            curved: !!w.curved,
          }));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && copiedWallsRef.current.length > 0) {
        e.preventDefault();
        const OFFSET = 1;
        const newIds = new Set();
        const baseTime = Date.now();
        copiedWallsRef.current.forEach((tpl, i) => {
          const id = `w${baseTime}_${i}`;
          newIds.add(id);
          addWall({
            id,
            pts: tpl.pts.map((p) => ({ x: p.x + OFFSET, y: p.y + OFFSET })),
            style: tpl.style,
            closed: tpl.closed,
            curved: tpl.curved,
          });
        });
        setSelectedWallIds(newIds);
      }
    };
    const upHandler = (e) => { if (e.key === 'Shift') shiftDownRef.current = false; };
    window.addEventListener('keydown', handler);
    window.addEventListener('keyup', upHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [editorMode, selectedWallIds, deleteWall, setDraftWall, walls, addWall,
      selectedPathIds, deletePath, setDraftPath, paths, addPath]);

  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage    = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer  = stage.getPointerPosition();
    const mp = { x: (pointer.x - stage.x()) / oldScale, y: (pointer.y - stage.y()) / oldScale };
    const direction = e.evt.deltaY < 0 ? 1 : -1;
    const newScale  = Math.min(MAX_SCALE, Math.max(MIN_SCALE, oldScale + direction * 0.08));
    setScale(newScale);
    setPos({ x: pointer.x - mp.x * newScale, y: pointer.y - mp.y * newScale });
  }, []);

  // ── Middle-mouse pan (always active, even in editor mode) ────────────────
  const midPan = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onDown = (e) => {
      if (e.button !== 1) return;          // middle button only
      e.preventDefault();
      midPan.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    };
    const onMove = (e) => {
      if (!midPan.current) return;
      setPos({
        x: midPan.current.origX + (e.clientX - midPan.current.startX),
        y: midPan.current.origY + (e.clientY - midPan.current.startY),
      });
    };
    const onUp = (e) => { if (e.button === 1) midPan.current = null; };
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [pos.x, pos.y]);

  const lastDist = useRef(0);
  const handleTouchMove = useCallback((e) => {
    const touches = e.evt.touches;
    if (touches.length !== 2) return;
    const dist = Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    if (lastDist.current) setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * (dist / lastDist.current))));
    lastDist.current = dist;
  }, []);

  const cursorStyle = editorMode === 'paint' ? 'crosshair'
    : editorMode === 'eraser'     ? 'cell'
    : editorMode === 'fill'       ? 'copy'
    : editorMode === 'fog-reveal' ? 'zoom-in'
    : editorMode === 'fog-hide'   ? 'zoom-out'
    : editorMode === 'spawn'      ? 'crosshair'
    : editorMode === 'wall-pen'    ? 'crosshair'
    : editorMode === 'wall-eraser' ? 'crosshair'
    : editorMode === 'wall-select' ? 'pointer'
    : editorMode === 'door-pen'    ? 'crosshair'
    : editorMode === 'wall-to-door' ? 'pointer'
    : editorMode === 'path-pen'    ? 'crosshair'
    : editorMode === 'path-eraser' ? 'crosshair'
    : editorMode === 'path-select' ? 'pointer'
    : editorMode === 'measure'     ? 'crosshair'
    : editorMode === 'elevation'   ? 'crosshair'
    : editorMode === 'teleport-pen' ? 'crosshair'
    : editorMode === 'trap-place'  ? 'crosshair'
    : onCellClick ? 'pointer' : 'grab';

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#0f0f1a', cursor: cursorStyle }}>
      <Stage
        ref={stageRef}
        width={size.w} height={size.h}
        scaleX={scale} scaleY={scale}
        x={pos.x} y={pos.y}
        draggable={!editorMode && !onCellClick}
        onDragStart={() => { didDrag.current = true; }}
        onDragEnd={(e) => { didDrag.current = true; setPos({ x: e.target.x(), y: e.target.y() }); }}
        onWheel={handleWheel}
        onTouchStart={() => { didDrag.current = false; }}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => { lastDist.current = 0; handleStageMouseUp(e); }}
        onMouseDown={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onDblClick={handleStageDblClick}
      >
        {/* Static stack — Grid + Terrain + Path + Wall + Door share one Konva
            canvas. Each child renders in `bare` mode (no inner <Layer>).
            DM view listens for wall/door/path clicks; player view doesn't. */}
        <Layer listening={isDm}>
          <GridLayer bare gridW={gridW} gridH={gridH} />
          <TerrainLayer bare gridW={gridW} gridH={gridH} />
          <PathLayer
            bare
            paths={paths}
            draftPts={draftPathPts}
            mousePos={pathMousePos}
            activeTool={editorMode}
            activeStyle={activePathStyle}
            draftCurved={pathCurved}
            selectedPathIds={selectedPathIds}
            dragOffset={pathDragOffset}
            onPathMouseDown={handlePathMouseDown}
            onSegmentClick={(pathId, segIdx) => removePathSegment(pathId, segIdx)}
          />
          <WallLayer
            bare
            walls={walls}
            draftPts={draftWallPts}
            mousePos={wallMousePos}
            activeTool={editorMode}
            activeStyle={activeWallStyle}
            draftCurved={wallCurved}
            selectedWallIds={selectedWallIds}
            dragOffset={wallDragOffset}
            onWallMouseDown={handleWallMouseDown}
            onWallClick={null}
            onSegmentClick={(wallId, segIdx) => removeWallSegment(wallId, segIdx)}
            onSegmentConvert={(wallId, segIdx, a, b, wallStyle) => {
              removeWallSegment(wallId, segIdx);
              const hp = activeDoorType === 'bars' ? 20 : 15;
              const isSecret = activeDoorType === 'secret';
              addDoor({
                id: `door_${Date.now()}`,
                pts: [a, b],
                type: activeDoorType,
                style: activeDoorStyle,
                state: 'closed',
                hp, maxHp: hp,
                secretRevealed: false,
                secretWallStyle: wallStyle,
                ...(isSecret ? { discoveryDC: 12 } : {}),
              });
            }}
          />
          <DoorSegmentLayer
            bare
            isDm={isDm}
            selectedDoorId={selectedDoorId}
            onDoorClick={onDoorClick}
            editorMode={editorMode}
            draftStart={draftDoorStart}
            draftMousePos={doorMousePos}
          />
          <TrapLayer bare isDm={isDm} />
          <TeleportLayer
            bare
            isDm={isDm}
            draftA={editorMode === 'teleport-pen' ? draftTeleportStart : null}
            draftMouse={editorMode === 'teleport-pen' ? teleportMousePos : null}
            draftStyle={activeTeleportStyle}
          />
        </Layer>

        {isDm ? (
          <>
            <FogLayer isDm={true} gridW={gridW} gridH={gridH} />
            <TokenLayer isDm={isDm} onTokenSelect={onTokenSelect} selectedTokenId={selectedTokenId} hoveredTokenId={hoveredTokenId} readOnly={readOnly} highlightTokenIds={highlightTokenIds} />
          </>
        ) : (
          <>
            <TokenLayer isDm={isDm} onTokenSelect={onTokenSelect} selectedTokenId={selectedTokenId} excludeId={myTokenId} readOnly={readOnly} />
            <FogLayer isDm={false} gridW={gridW} gridH={gridH} />
            {myTokenId && (
              <TokenLayer isDm={isDm} onTokenSelect={onTokenSelect} selectedTokenId={selectedTokenId} onlyId={myTokenId} readOnly={readOnly} />
            )}
          </>
        )}

        {/* Overlay stack — Pings + Measure + Spawn share one Konva canvas. */}
        <Layer listening={false}>
          <PingLayer bare />
          <MeasureLayer bare from={measureFrom} to={measureTo} />
          {isDm && spawnPoint && (
            <Group x={spawnPoint.x * CELL_SIZE} y={spawnPoint.y * CELL_SIZE}>
              <Rect x={0} y={0} width={CELL_SIZE * 3} height={CELL_SIZE * 2} fill="rgba(74,222,128,0.10)" stroke="#4ade80" strokeWidth={1.5} dash={[5, 3]} cornerRadius={4} />
              {[0,1,2].map((cx) => [0,1].map((cy) => (
                <Rect key={`sp-${cx}-${cy}`} x={cx * CELL_SIZE + 2} y={cy * CELL_SIZE + 2} width={CELL_SIZE - 4} height={CELL_SIZE - 4} stroke="rgba(74,222,128,0.2)" strokeWidth={1} fill="transparent" cornerRadius={2} />
              )))}
              <Text x={0} y={CELL_SIZE - 9} width={CELL_SIZE * 3} text="🏠 Spawn" fontSize={12} fontStyle="bold" fill="#4ade80" align="center" />
            </Group>
          )}
        </Layer>
      </Stage>

      {/* Ambient lighting tint — was a separate Konva Layer (extra canvas);
          now a CSS overlay with mix-blend-mode so the browser's GPU
          compositor handles the multiply at zero JS cost. The DM zoom /
          editor UI overlays sit on top via higher z-index, so they stay
          un-tinted. */}
      {ambient && ambient.alpha > 0 && (
        <div
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: ambient.color, opacity: ambient.alpha,
            mixBlendMode: 'multiply', zIndex: 1,
          }}
        />
      )}

      <div style={styles.zoomControls}>
        <button style={styles.zoomBtn} onClick={() => setScale((s) => Math.min(MAX_SCALE, s + 0.15))}>+</button>
        <span style={styles.zoomLabel}>{Math.round(scale * 100)}%</span>
        <button style={styles.zoomBtn} onClick={() => setScale((s) => Math.max(MIN_SCALE, s - 0.15))}>−</button>
        <button
          style={{ ...styles.zoomBtn, fontSize: 12 }}
          onClick={() => {
            const myToken = myTokenId ? tokens[myTokenId] : null;
            if (myToken) {
              const nextScale = 1;
              const cx = (myToken.x + 0.5) * CELL_SIZE;
              const cy = (myToken.y + 0.5) * CELL_SIZE;
              setScale(nextScale);
              setPos({ x: size.w / 2 - cx * nextScale, y: size.h / 2 - cy * nextScale });
            } else {
              setScale(1);
              setPos({ x: 0, y: 0 });
            }
          }}
        >⌂</button>
      </div>
    </div>
  );
});

export default MapCanvas;

const styles = {
  zoomControls: { position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(26,26,46,0.9)', borderRadius: 10, padding: '8px 6px', border: '1px solid #333355', backdropFilter: 'blur(6px)', zIndex: 10 },
  zoomBtn:      { width: 32, height: 32, background: '#242440', color: '#e2e2f0', border: '1px solid #333355', borderRadius: 6, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  zoomLabel:    { fontSize: 11, color: '#888aaa', userSelect: 'none' },
};
